import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase-client';
import { useAuth } from '../contexts/auth-context';
import { useMemo } from 'react';
import type { Appointment as AppAppointment, AppointmentStatus } from '@/types/appointment-types';

// Raw row returned by the appointment_bookings table
interface AppointmentBookingRow {
  id: string;
  session_id: string | null;
  user_id: string;
  appointment_type: string | null;
  preferred_date: string | null;
  preferred_time: string | null;
  clinic_location: string | null;
  status: string;
  notes: string | null;
  created_at: string | null;
  updated_at: string | null;
  service_code: string | null;
  provider_id: string | null;
  priority: string | null;
  reason: string | null;
  scheduled_at: string | null;
  scheduled_by: string | null;
  slot_id: string | null;
}

// Insert payload for appointment_bookings
type AppointmentBookingInsert = Omit<AppointmentBookingRow, 'id' | 'created_at' | 'updated_at'> & {
  id?: string;
  created_at?: string | null;
  updated_at?: string | null;
};

// Update payload for appointment_bookings
type AppointmentBookingUpdate = Partial<AppointmentBookingInsert>;

// --- Filter Types ---
export interface AppointmentsFilter {
  status?: AppointmentStatus[];
  dateRange?: { start: string; end: string };
  searchTerm?: string;
}

// --- Return type ---
export interface UseAppointmentsResult {
  appointments: AppAppointment[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  createAppointment: (data: Omit<AppointmentBookingInsert, 'user_id'>) => Promise<AppointmentBookingRow>;
  updateAppointment: (data: AppointmentBookingUpdate & { id: string }) => Promise<AppointmentBookingRow>;
  cancelAppointment: (id: string) => Promise<AppointmentBookingRow>;
  createAppointmentStatus: string;
  updateAppointmentStatus: string;
  cancelAppointmentStatus: string;
  refetch: () => void;
}

/**
 * Maps a raw appointment_bookings row to the frontend AppAppointment type.
 *
 * appointment_bookings uses preferred_date + preferred_time for the scheduled
 * time. If scheduled_at is present it takes precedence as it represents the
 * confirmed timestamp. Columns that have no equivalent in the real schema are
 * given sensible defaults so the rest of the UI doesn't break.
 */
const mapRowToAppAppointment = (row: AppointmentBookingRow): AppAppointment => {
  // Prefer scheduled_at; fall back to preferred_date + preferred_time
  let startTime: string;
  if (row.scheduled_at) {
    startTime = row.scheduled_at;
  } else if (row.preferred_date && row.preferred_time) {
    startTime = `${row.preferred_date}T${row.preferred_time}`;
  } else if (row.preferred_date) {
    startTime = `${row.preferred_date}T00:00:00`;
  } else {
    startTime = new Date(0).toISOString();
  }

  // appointment_bookings has no duration — default end time to 30 min after start
  const endTime = new Date(new Date(startTime).getTime() + 30 * 60_000).toISOString();

  return {
    id: row.id,
    // user_id is TEXT in appointment_bookings; doctorId/patientId are required
    patientId: row.user_id,
    providerId: row.provider_id ?? undefined,
    doctorId: row.provider_id ?? '',
    providerName: undefined, // No provider name join available in appointment_bookings
    facilityId: row.clinic_location ?? '',
    appointmentType: (row.appointment_type ?? undefined) as AppAppointment['appointmentType'],
    startTime,
    endTime,
    status: row.status as AppointmentStatus,
    reason: row.reason ?? undefined,
    notes: row.notes ?? undefined,
    createdBy: row.scheduled_by ?? 'unknown',
    createdAt: row.created_at ?? new Date(0).toISOString(),
    updatedAt: row.updated_at ?? new Date(0).toISOString(),
  };
};

// --- Hook ---
export const useAppointments = (filters?: AppointmentsFilter): UseAppointmentsResult => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const fetchAppointments = async (currentFilters?: AppointmentsFilter): Promise<AppAppointment[]> => {
    if (!user?.id) {
      throw new Error('User not authenticated');
    }

    // user_id in appointment_bookings is TEXT, so we cast the UUID to text
    let query = supabase
      .from('appointment_bookings')
      .select('*')
      .eq('user_id', user.id as string);

    if (currentFilters?.status && currentFilters.status.length > 0) {
      query = query.in('status', currentFilters.status);
    }

    if (currentFilters?.dateRange?.start) {
      query = query.gte('preferred_date', currentFilters.dateRange.start);
    }
    if (currentFilters?.dateRange?.end) {
      query = query.lte('preferred_date', currentFilters.dateRange.end);
    }
    if (currentFilters?.searchTerm) {
      const term = `%${currentFilters.searchTerm}%`;
      query = query.or(
        `appointment_type.ilike.${term},reason.ilike.${term},clinic_location.ilike.${term}`
      );
    }

    // Order by preferred_date then preferred_time; fall back gracefully
    query = query
      .order('preferred_date', { ascending: true })
      .order('preferred_time', { ascending: true });

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching appointments:', error);
      throw new Error(error.message);
    }

    return (data || []).map((row: any) => mapRowToAppAppointment(row as AppointmentBookingRow));
  };

  // --- Mutations ---

  const createAppointment = async (
    appointmentData: Omit<AppointmentBookingInsert, 'user_id'>
  ): Promise<AppointmentBookingRow> => {
    if (!user?.id) {
      throw new Error('User not authenticated');
    }

    const payload: AppointmentBookingInsert = {
      ...appointmentData,
      user_id: user.id,
      status: appointmentData.status || 'pending',
    };

    const { data, error } = await supabase
      .from('appointment_bookings')
      .insert(payload)
      .select()
      .single();

    if (error) {
      console.error('Error creating appointment:', error);
      throw new Error(error.message);
    }
    if (!data) throw new Error('No data returned after creating appointment');
    return data as AppointmentBookingRow;
  };

  const updateAppointment = async ({
    id,
    ...updates
  }: AppointmentBookingUpdate & { id: string }): Promise<AppointmentBookingRow> => {
    const { data, error } = await supabase
      .from('appointment_bookings')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating appointment:', error);
      throw new Error(error.message);
    }
    if (!data) throw new Error('No data returned after updating appointment');
    return data as AppointmentBookingRow;
  };

  const cancelAppointment = async (id: string): Promise<AppointmentBookingRow> => {
    return updateAppointment({ id, status: 'cancelled' });
  };

  // --- React Query ---
  const appointmentsQuery = useQuery({
    queryKey: ['appointments', user?.id, filters],
    queryFn: () => fetchAppointments(filters),
    enabled: !!user?.id,
  });

  const createAppointmentMutation = useMutation({
    mutationFn: createAppointment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments', user?.id] });
    },
  });

  const updateAppointmentMutation = useMutation({
    mutationFn: updateAppointment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments', user?.id] });
    },
  });

  const cancelAppointmentMutation = useMutation({
    mutationFn: cancelAppointment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments', user?.id] });
    },
  });

  return {
    appointments: appointmentsQuery.data || [],
    isLoading: appointmentsQuery.isLoading,
    isError: appointmentsQuery.isError,
    error: appointmentsQuery.error as Error | null,
    createAppointment: createAppointmentMutation.mutateAsync,
    updateAppointment: updateAppointmentMutation.mutateAsync,
    cancelAppointment: cancelAppointmentMutation.mutateAsync,
    createAppointmentStatus: createAppointmentMutation.status,
    updateAppointmentStatus: updateAppointmentMutation.status,
    cancelAppointmentStatus: cancelAppointmentMutation.status,
    refetch: appointmentsQuery.refetch,
  };
};

/**
 * Hook to fetch only upcoming appointments.
 * Wraps useAppointments and filters client-side for future non-cancelled entries.
 */
export interface UseUpcomingAppointmentsOptions {
  limit?: number;
}

export const useUpcomingAppointments = (options: UseUpcomingAppointmentsOptions = {}) => {
  const { limit = 5 } = options;

  const { appointments, isLoading, isError, error, refetch } = useAppointments();

  const upcomingAppointments = useMemo(() => {
    if (!appointments) return [];
    const now = new Date();

    return appointments
      .filter((appointment) => {
        const start = new Date(appointment.startTime);
        return !isNaN(start.getTime()) && start >= now && appointment.status !== 'cancelled';
      })
      .sort((a, b) => {
        const startA = new Date(a.startTime).getTime();
        const startB = new Date(b.startTime).getTime();
        if (isNaN(startA) || isNaN(startB)) return 0;
        return startA - startB;
      })
      .slice(0, limit);
  }, [appointments, limit]);

  return { data: upcomingAppointments, isLoading, isError, error, refetch };
};

export default useAppointments;
